import { useEffect, useRef, useState } from 'react'
import { useAllDms } from '../queries/useDms.js'
import { useUIStore } from '../store/uiStore.js'
import { colorFor } from '../data/userColors.js'

/**
 * DmToast — bottom-right notification when a new DM arrives from someone
 * else. Click to open that thread.
 *
 * We watch all threads for new messages since the last render. If a new
 * message exists from an author that isn't the current user, and the
 * related DM panel isn't already open, show a toast.
 *
 * Similar pattern to MentionToast.
 */
export default function DmToast() {
  const currentUser = useUIStore((s) => s.currentUser)
  const openDm = useUIStore((s) => s.openDm)
  const openDmWith = useUIStore((s) => s.openDmWith)
  const { threads } = useAllDms()

  const lastSeenIdsRef = useRef({})
  // Timestamp when this component mounted. Any message with an `at`
  // older than this is historical and must never trigger a toast. See
  // the MentionToast fix for the same reasoning: priming on first
  // effect-run was unreliable because meta often hadn't resolved yet,
  // so old messages re-toasted on every refresh.
  const firstLoadAt = useRef(Date.now())
  const [toast, setToast] = useState(null)

  useEffect(() => {
    if (!currentUser) return

    for (const { key, thread, other } of threads) {
      const msgs = thread.messages || []
      const last = msgs[msgs.length - 1]
      if (!last) continue
      if (lastSeenIdsRef.current[key] === last.id) continue
      lastSeenIdsRef.current[key] = last.id

      // Skip anything created before the page loaded — it's history.
      const msgMs = last.at ? new Date(last.at).getTime() : 0
      if (!msgMs || msgMs < firstLoadAt.current) continue

      if (last.author === currentUser) continue
      const otherParticipant = other?.[0]
      const alreadyOpen = openDm?.participants?.[0] === otherParticipant
      if (alreadyOpen) continue

      setToast({
        from: last.author,
        preview:
          (last.text || '').length > 60
            ? last.text.slice(0, 60) + '…'
            : last.text,
        other: otherParticipant,
      })
      return // one toast at a time
    }
  }, [threads, currentUser, openDm])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 8000)
    return () => clearTimeout(t)
  }, [toast])

  if (!toast) return null

  const color = colorFor(toast.from)

  return (
    <div
      onClick={() => {
        openDmWith(toast.other)
        setToast(null)
      }}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 320,
        background: '#fff',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${color}`,
        borderRadius: 10,
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
        padding: '12px 14px',
        zIndex: 1000,
        cursor: 'pointer',
        animation: 'slideInRight 0.3s ease-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: color,
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
          }}
        >
          {toast.from?.[0]}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
          {toast.from}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setToast(null)
          }}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            color: 'var(--muted-light)',
            cursor: 'pointer',
            fontSize: 14,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
        {toast.preview}
      </div>
      <div style={{ fontSize: 10, color: 'var(--muted-light)', fontStyle: 'italic', marginTop: 6 }}>
        click to open
      </div>
    </div>
  )
}
